import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Users, Square, Wifi, Music, Tv, Wine, ChevronLeft, ChevronRight, Sparkles } from 'lucide-react';
import { Button } from './ui/Button';
interface Room {
  id: string;
  name: string;
  type: string;
  description: string;
  capacity: number;
  size: number;
  price: number;
  images: string[];
  amenities: string[];
  features: string[];
  badge?: string;
}
interface RoomCardProps {
  room: Room;
  onBook: (room: Room) => void;
}
export function RoomCard({
  room,
  onBook
}: RoomCardProps) {
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [isHovered, setIsHovered] = useState(false);
  const amenityIcons: {
    [key: string]: any;
  } = {
    WiFi: Wifi,
    'Sound System': Music,
    'Smart TV': Tv,
    'Mini Bar': Wine
  };
  const nextImage = (e: React.MouseEvent) => {
    e.stopPropagation();
    setCurrentImageIndex(prev => (prev + 1) % room.images.length);
  };
  const prevImage = (e: React.MouseEvent) => {
    e.stopPropagation();
    setCurrentImageIndex(prev => (prev - 1 + room.images.length) % room.images.length);
  };
  return <motion.div initial={{
    opacity: 0,
    y: 20
  }} animate={{
    opacity: 1,
    y: 0
  }} className="group relative bg-gradient-to-br from-[#1A2228] to-[#0F161B] border border-white/10 rounded-2xl overflow-hidden hover:border-[#DDA247]/50 transition-all duration-500 hover:shadow-[0_0_60px_rgba(221,162,71,0.15)]" onMouseEnter={() => setIsHovered(true)} onMouseLeave={() => setIsHovered(false)} data-id="element-575">
      {/* Badge */}
      {room.badge && <div className="absolute top-4 left-4 z-20" data-id="element-576">
          <div className="bg-[#DDA247] text-[#0B1014] px-3 py-1.5 rounded-full text-xs font-bold uppercase tracking-wider flex items-center gap-1.5 shadow-lg" data-id="element-577">
            <Sparkles size={12} data-id="element-578" />
            {room.badge}
          </div>
        </div>}

      {/* Image Carousel */}
      <div className="relative h-72 overflow-hidden bg-[#0B1014]" data-id="element-579">
        <AnimatePresence mode="wait" data-id="element-580">
          <motion.div key={currentImageIndex} initial={{
          opacity: 0,
          scale: 1.1
        }} animate={{
          opacity: 1,
          scale: 1
        }} exit={{
          opacity: 0,
          scale: 0.95
        }} transition={{
          duration: 0.5
        }} className="absolute inset-0" data-id="element-581">
            <div className="w-full h-full bg-cover bg-center" style={{
            backgroundImage: `url(${room.images[currentImageIndex]})`
          }} data-id="element-582" />
            {/* Gradient overlay */}
            <div className="absolute inset-0 bg-gradient-to-t from-[#0B1014] via-transparent to-transparent opacity-60" data-id="element-583" />
          </motion.div>
        </AnimatePresence>

        {/* Image Navigation */}
        {room.images.length > 1 && <>
            <button onClick={prevImage} className="absolute left-4 top-1/2 -translate-y-1/2 w-10 h-10 bg-[#0B1014]/80 backdrop-blur-sm text-white rounded-full flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity hover:bg-[#DDA247] hover:text-[#0B1014] z-10" data-id="element-584">
              <ChevronLeft size={20} data-id="element-585" />
            </button>
            <button onClick={nextImage} className="absolute right-4 top-1/2 -translate-y-1/2 w-10 h-10 bg-[#0B1014]/80 backdrop-blur-sm text-white rounded-full flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity hover:bg-[#DDA247] hover:text-[#0B1014] z-10" data-id="element-586">
              <ChevronRight size={20} data-id="element-587" />
            </button>

            {/* Image Indicators */}
            <div className="absolute bottom-4 left-1/2 -translate-x-1/2 flex gap-2 z-10" data-id="element-588">
              {room.images.map((_, index) => <button key={index} onClick={e => {
            e.stopPropagation();
            setCurrentImageIndex(index);
          }} className={`h-1 rounded-full transition-all ${index === currentImageIndex ? 'w-8 bg-[#DDA247]' : 'w-1 bg-white/30 hover:bg-white/50'}`} data-id="element-589" />)}
            </div>
          </>}
      </div>

      {/* Content */}
      <div className="p-6 space-y-6" data-id="element-590">
        {/* Header */}
        <div data-id="element-591">
          <div className="flex items-start justify-between mb-2" data-id="element-592">
            <div data-id="element-593">
              <h3 className="text-xl font-bold text-white mb-1 tracking-wide" data-id="element-594">
                {room.name}
              </h3>
              <p className="text-xs text-gray-500 uppercase tracking-widest" data-id="element-595">
                {room.type}
              </p>
            </div>
            <div className="text-right" data-id="element-596">
              <p className="text-xs text-gray-500 mb-1" data-id="element-597">Starting at</p>
              <p className="text-2xl font-bold text-[#DDA247]" data-id="element-598">
                ${room.price.toLocaleString()}
              </p>
              <p className="text-[10px] text-gray-600" data-id="element-599">per night</p>
            </div>
          </div>
          <p className="text-sm text-gray-400 leading-relaxed" data-id="element-600">
            {room.description}
          </p>
        </div>

        {/* Quick Stats */}
        <div className="flex items-center gap-6 pb-6 border-b border-white/5" data-id="element-601">
          <div className="flex items-center gap-2" data-id="element-602">
            <Users className="w-4 h-4 text-[#DDA247]" data-id="element-603" />
            <span className="text-sm text-gray-300" data-id="element-604">Up to {room.capacity}</span>
          </div>
          <div className="flex items-center gap-2" data-id="element-605">
            <Square className="w-4 h-4 text-[#DDA247]" data-id="element-606" />
            <span className="text-sm text-gray-300" data-id="element-607">{room.size} sq ft</span>
          </div>
        </div>

        {/* Amenities */}
        <div data-id="element-608">
          <p className="text-xs text-gray-500 uppercase tracking-wider mb-3" data-id="element-609">
            Key Amenities
          </p>
          <div className="flex flex-wrap gap-2" data-id="element-610">
            {room.amenities.slice(0, 4).map((amenity, index) => {
            const Icon = amenityIcons[amenity] || Wifi;
            return <div key={index} className="flex items-center gap-1.5 px-3 py-1.5 bg-[#0B1014]/50 border border-white/5 rounded-lg text-xs text-gray-300" data-id="element-611">
                  <Icon size={12} className="text-[#DDA247]" data-id="element-612" />
                  {amenity}
                </div>;
          })}
          </div>
        </div>

        {/* Features */}
        <div data-id="element-613">
          <p className="text-xs text-gray-500 uppercase tracking-wider mb-3" data-id="element-614">
            Included Features
          </p>
          <ul className="grid grid-cols-2 gap-2" data-id="element-615">
            {room.features.slice(0, 4).map((feature, index) => <li key={index} className="flex items-center gap-2 text-xs text-gray-400" data-id="element-616">
                <div className="w-1 h-1 rounded-full bg-[#DDA247]" data-id="element-617" />
                {feature}
              </li>)}
          </ul>
        </div>

        {/* CTA */}
        <Button variant="primary" fullWidth onClick={() => onBook(room)} className="group" data-id="element-618">
          <span className="flex items-center justify-center gap-2" data-id="element-619">
            Book This Room
            <motion.span animate={{
            x: isHovered ? 5 : 0
          }} transition={{
            type: 'spring',
            stiffness: 400,
            damping: 25
          }} data-id="element-620">
              →
            </motion.span>
          </span>
        </Button>
      </div>

      {/* Hover glow effect */}
      <motion.div animate={{
      opacity: isHovered ? 1 : 0
    }} className="absolute inset-0 bg-gradient-to-br from-[#DDA247]/0 via-[#DDA247]/0 to-[#DDA247]/5 pointer-events-none" data-id="element-621" />
    </motion.div>;
}